<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import { useMachinesStore } from "@/stores/machines";
import { lastSeenLabel } from "@/utils/lastSeen";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const machines = useMachinesStore();

const agentId = computed(() => route.params.agentId as string);
const agent = computed(() => machines.agents.find((a) => a.id === agentId.value));
const sessions = computed(() => machines.sessionsForAgent(agentId.value));
const actionError = ref<string | null>(null);
const stopping = ref<string | null>(null);

onMounted(() => void machines.start());
onBeforeUnmount(() => machines.stop());

function presenceTone(p?: string) {
  return p === "online" ? "ok" : "bad";
}

const offlineSince = computed(() => {
  const a = agent.value;
  if (!a || a.presence === "online") return null;
  const rel = lastSeenLabel(a.last_seen_at, locale.value);
  return rel ? t("home.last_seen", { rel }) : null;
});

function startSession() {
  router.push({ name: "new-session", query: { agent_id: agentId.value } });
}

function attachSession(sessionId: string) {
  router.push({ name: "session", params: { sessionId } });
}

async function stopSession(sessionId: string) {
  if (stopping.value) return;
  stopping.value = sessionId;
  actionError.value = null;
  try {
    await api.sessions.stop(agentId.value, sessionId);
  } catch (err) {
    actionError.value = describeError(err, t);
  } finally {
    stopping.value = null;
  }
}
</script>

<template>
  <section class="agent">
    <header class="agent__header">
      <RouterLink class="agent__back" :to="{ name: 'home' }">← {{ t("agent.back") }}</RouterLink>
      <h1 v-if="agent">{{ agent.display_name || agent.hostname }}</h1>
      <h1 v-else class="agent__title-dim">
        {{ machines.state.kind === "ready" ? t("agent.not_found") : t("admin.loading") }}
      </h1>
      <span v-if="agent" class="admin-badge" :data-tone="presenceTone(agent.presence)">
        {{ agent.presence === "online" ? t("home.online") : t("home.offline") }}
      </span>
      <small v-if="offlineSince" class="agent__last-seen">{{ offlineSince }}</small>
    </header>

    <p v-if="machines.state.kind === 'error'" class="admin-error">{{ machines.state.message }}</p>
    <p v-else-if="!agent && machines.state.kind === 'ready'" class="admin-empty">
      {{ t("agent.not_found") }}
    </p>
    <div v-else-if="!agent" class="loading-note" role="status" aria-live="polite">
      <span class="spinner" aria-hidden="true"></span> {{ t("admin.loading") }}
    </div>

    <template v-if="agent">
      <dl class="kv">
        <dt>{{ t("home.machine_login") }}</dt>
        <dd>{{ agent.login }}</dd>
        <dt>{{ t("home.machine_host") }}</dt>
        <dd>{{ agent.hostname }}</dd>
        <dt>{{ t("home.machine_version") }}</dt>
        <dd>{{ agent.version ?? "—" }}</dd>
        <dt>{{ t("admin.status") }}</dt>
        <dd>{{ t(`admin.status_${agent.status}`) }}</dd>
        <dt>{{ t("admin.update.channel") }}</dt>
        <dd>{{ agent.update_channel }}</dd>
        <dt>{{ t("admin.update.policy") }}</dt>
        <dd>{{ agent.update_policy }}</dd>
      </dl>

      <h2 class="agent__subtitle">{{ t("home.machine_sessions") }}</h2>
      <p v-if="actionError" class="admin-error">{{ actionError }}</p>
      <ul v-if="sessions.length" class="sessions">
        <li v-for="s in sessions" :key="s.id" class="sessions__item">
          <button type="button" class="sessions__attach" @click="attachSession(s.id)">
            <span class="sessions__engine">{{ s.engine }}</span>
            <span class="sessions__status" :data-tone="s.status === 'running' ? 'ok' : 'warn'">{{
              s.status
            }}</span>
            <span class="sessions__go">{{ t("home.attach") }} →</span>
          </button>
          <button
            type="button"
            class="sessions__stop"
            :disabled="stopping === s.id"
            @click="stopSession(s.id)"
          >
            {{ stopping === s.id ? "…" : t("agent.stop") }}
          </button>
        </li>
      </ul>
      <p v-else class="sessions__empty">{{ t("home.no_active_sessions") }}</p>

      <button
        type="button"
        class="agent__action"
        :disabled="agent.presence !== 'online' || agent.status !== 'enabled'"
        @click="startSession"
      >
        {{ t("agent.start_session") }}
      </button>
    </template>
  </section>
</template>

<style scoped>
.agent {
  max-width: 48rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.agent__header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}
.agent__header h1 {
  margin: 0;
  font-size: 1.2rem;
}
.agent__last-seen {
  color: var(--color-fg-dim);
  font-size: 0.85em;
}

.agent__title-dim {
  color: var(--color-fg-dim);
  font-weight: 500;
}
.agent__back {
  font-size: 0.85rem;
}
.agent__subtitle {
  font-size: 0.95rem;
  margin: 0.5rem 0 0;
  color: var(--color-fg-dim);
}
.sessions {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.sessions__item {
  display: flex;
  gap: 0.4rem;
}
.sessions__attach {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.6rem;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  color: var(--color-fg);
  cursor: pointer;
  text-align: left;
  min-height: 44px;
}
.sessions__engine {
  font-weight: 600;
}
.sessions__go {
  margin-left: auto;
  color: var(--color-fg-dim);
}
.sessions__stop {
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg-elev);
  color: var(--color-fg);
  cursor: pointer;
  padding: 0 0.75rem;
}
.sessions__empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-fg-dim);
}
.agent__action {
  align-self: flex-start;
  padding: 0.5rem 0.95rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
}
.agent__action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
